import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  Box,
  ChangeRequestDialog,
  ContentTemplate,
  filterAccountManagementActionTypes,
  prepareFiles,
  Progress,
  SectionHeader,
  TitleHeader,
  useSnackbarHandler,
} from 'web-library';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { FunctionModule } from 'web-library/models/UserGroupAccessConfiguration/UserGroupAccessConfiguration';
import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import EditButton from 'web-library/components/buttons/EditButton';
import { AUTHORITY_NAMES, FUNCTION_NAMES } from 'web-library/models/AuthorityFunctions';
import { AccountManagementActionTypesResponseObject } from 'web-library/models/AccountManagementConfiguration';
import { MutationStatus } from 'react-query';
import { UploadedFile } from 'web-library/models/UploadedFile';
import { usePostSearchApprovers } from '../../../api-refactored/user-group-access-configuration/usePostSearchApprovers';
import CandidatePersonalParticulars from './CandidatePersonalParticulars';
import CandidateContactInformation from './CandidateContactInformation';
import CandidateNextOfKinDetails from './CandidateNextOfKinDetails';
import { useGetCandidateProfile } from '../../../api-refactored/account-management/useGetCandidateProfile';
import CreateEditCandidateSchema, {
  CreateEditCandidateSchemaData,
} from './CreateEditCandidateSchema';
import { usePutUpdateCandidateProfile } from '../../../api-refactored/account-management/usePutUpdateCandidateProfile';
import { usePostCreateCandidateProfile } from '../../../api-refactored/account-management/usePostCreateCandidateProfile';
import { useGetAccountManagementActionTypes } from '../../../api-refactored/account-management/useGetAccountManagementActionTypes';
import { usePostAccountManagementAction } from '../../../api-refactored/account-management/usePostAccountManagementAction';
import { useGetIExamMasterCode } from '../../../api-refactored/user/useGetIExamMasterCode';
import { useGetSchoolsByType } from '../../../api-refactored/non-national-exam-registration/useGetSchoolsByType';
import { useGetUserAuthorities } from '../../../api-refactored/user/useGetUserAuthorities';
import RouteKey from '../../../routes/key';
import { AccountTypeMode } from '../UserAccountManagementListing';
import { usePostRequestFilePresigned } from '../../../api-refactored/user-group-access-configuration/usePostRequestFilePresigned';
import ManageAccountContent from '../CreateEditViewAdminAccountDetails/ManageAccountContent';
import ActionApproverRemarksContent from '../CreateEditViewAdminAccountDetails/ActionApproverRemarksContent';
import AccountManagementMiscellaneousComponents from '../CreateEditViewAdminAccountDetails/AccountManagementMiscellaneousComponents';
import {
  useCandidateContactInformation,
  useCandidateNationalityDetails,
  useCandidateNextOfKinDetails,
  useCandidatePayloadValues,
  useCandidatePersonalParticulars,
} from './useCreateEditViewCandidateAccountDetails';
import { useStyles } from '../CreateEditViewAdminAccountDetails';

const { admin } = RouteKey.child;
const { userAccountManagement } = RouteKey.admin.child;

export interface CreateEditViewCandidateAccountProps {
  isEdit: boolean;
}

const CreateEditViewCandidateAccount = ({ isEdit }: CreateEditViewCandidateAccountProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { snackbarHandler } = useSnackbarHandler();
  const history = useHistory();
  const { uuid } = useParams<{ uuid: string }>();

  const [isEditState, setIsEditState] = useState<boolean>(!isEdit);
  const [isApproverNeededAction, setIsApproverNeededAction] = useState<boolean>(false);

  const [errorMessage, setErrorMessage] = useState<string>('');

  const [filteredActionTypes, setFilteredActionTypes] = useState<
    AccountManagementActionTypesResponseObject[]
  >([]);

  const [apiButtonStatus, setApiButtonStatus] = useState<MutationStatus>('idle');

  const [files, setFiles] = useState<UploadedFile[]>([]);

  const [shouldSubmit, setShouldSubmit] = useState<boolean>(false);

  const [uploadFileErrorMessage, setUploadFileErrorMessage] = useState<string>('');

  const [openConfirmChangesModal, setOpenConfirmChangesModal] = useState<boolean>(false);

  const [deleteAccountModalMessage, setDeleteAccountModalMessage] = useState<string>('');

  const {
    data: candidateProfileDetails,
    refetch: refetchCandidateProfileDetails,
  } = useGetCandidateProfile({
    uuid,
    enabled: !!uuid && isEdit,
    onSuccess: () => {
      setFiles([]);
      setErrorMessage('');
      setUploadFileErrorMessage('');
      refetchAccountManagementActionTypes();
    },
  });

  const { refetch: refetchAccountManagementActionTypes } = useGetAccountManagementActionTypes({
    onSuccess: (res) =>
      filterAccountManagementActionTypes(
        res,
        candidateProfileDetails?.candidateInfoDetail.activeStatus as string,
        candidateProfileDetails?.candidateInfoDetail.blockStatus as string,
        candidateProfileDetails?.candidateInfoDetail.lockStatus as string,
        candidateProfileDetails?.candidateInfoDetail.accountType as string,
        setFilteredActionTypes
      ),
  });

  const { data: authorities } = useGetUserAuthorities();

  const {
    mutate: mutatePostSearchApprovers,
    data: postSearchApproversData,
  } = usePostSearchApprovers({});

  const hasEditPermission = authorities?.includes(AUTHORITY_NAMES.CP_ADMIN_USER_ACCOUNTS_EDIT);

  const { mutate: mutatePostAccountManagementAction } = usePostAccountManagementAction({
    onSuccess: () => {
      setApiButtonStatus('success');
      setShouldSubmit(false);
      handleBackToCandidateListingWithAlert();
    },
    onError: (err) => {
      setErrorMessage(err?.response?.data?.message!);
      setApiButtonStatus('error');
      setShouldSubmit(false);
    },
  });

  const { data: iExamsData } = useGetIExamMasterCode();

  const { data: dataOrganisation } = useGetSchoolsByType(true, 'NMOE');

  const {
    defaultDateOfBirth,
    genderDefaultObject,
    raceDefaultObject,
    occupationDefaultObject,
    schoolDefaultObject,
    isMOESchool,
    disabledFields,
  } = useCandidatePersonalParticulars({
    iExamsData,
    candidateProfileDetails: candidateProfileDetails?.candidateInfoDetail,
    dataOrganisation,
    isTaskDetails: false,
    isEdit,
    isEditState,
    isApproverNeededAction,
  });

  const {
    mobileNumberCountryCodeDefaultObject,
    telephoneNumberCountryCodeDefaultObject,
  } = useCandidateContactInformation({
    iExamsData,
    candidateProfileDetails: candidateProfileDetails?.candidateInfoDetail,
  });

  const {
    relationshipDefaultObject,
    nextOfKinCountryCodeDefaultObject,
  } = useCandidateNextOfKinDetails({
    iExamsData,
    candidateProfileDetails: candidateProfileDetails?.candidateInfoDetail,
  });

  const {
    citizenshipDefaultObject,
    defaultNationalityIssueDate,
    prStatusDefaultObject,
    nationalityDefaultObject,
  } = useCandidateNationalityDetails({
    iExamsData,
    candidateProfileDetails: candidateProfileDetails?.candidateInfoDetail,
  });

  const defaultMOESchoolObject = useMemo(
    () => ({
      id: 0,
      name: candidateProfileDetails?.candidateInfoDetail?.school,
      uuid: '',
    }),
    [candidateProfileDetails?.candidateInfoDetail?.school]
  );

  const IdTypeDefaultValue = useMemo(() => {
    if (candidateProfileDetails === undefined) {
      return null;
    }
    return candidateProfileDetails?.candidateInfoDetail.userIdType === 'NRIC/FIN' ? 'I' : 'P';
  }, [candidateProfileDetails]);

  const formDefaultValues = useMemo(
    () =>
      ({
        name: candidateProfileDetails?.candidateInfoDetail.name,
        hanyupinyinName: candidateProfileDetails?.candidateInfoDetail.hanyuPinyinName,
        IdType: IdTypeDefaultValue,
        NricFin: candidateProfileDetails?.candidateInfoDetail.identity,
        citizenship: citizenshipDefaultObject,
        prStatus: prStatusDefaultObject,
        dateOfBirth: defaultDateOfBirth,
        gender: genderDefaultObject,
        race: raceDefaultObject,
        occupation: occupationDefaultObject,
        school: isMOESchool ? defaultMOESchoolObject : schoolDefaultObject,
        email: candidateProfileDetails?.candidateInfoDetail.email,
        nationalityIssueDate: defaultNationalityIssueDate,
        mobileCountryCode: mobileNumberCountryCodeDefaultObject,
        mobileContactNumber: candidateProfileDetails?.candidateInfoDetail.mobileNumber,
        telephoneCountryCode: telephoneNumberCountryCodeDefaultObject,
        telephoneContactNumber: candidateProfileDetails?.candidateInfoDetail.telephoneNumber,
        houseOrBlkNo: candidateProfileDetails?.candidateInfoDetail.localAddressBlockNumber,
        streetName: candidateProfileDetails?.candidateInfoDetail.localAddressStreetName,
        building: candidateProfileDetails?.candidateInfoDetail.localAddressBuildingName,
        floor: candidateProfileDetails?.candidateInfoDetail.localAddressFloorNumber,
        unit: candidateProfileDetails?.candidateInfoDetail.localAddressUnitNumber,
        postalCode: candidateProfileDetails?.candidateInfoDetail.localAddressPostalCode,
        preferredAddress:
          candidateProfileDetails?.candidateInfoDetail.localAddressPreferred === undefined ||
          candidateProfileDetails?.candidateInfoDetail.localAddressPreferred
            ? 'L'
            : 'F',
        preferredTelephoneOrMobile:
          candidateProfileDetails?.candidateInfoDetail.mobileNumberPreferred === undefined ||
          candidateProfileDetails?.candidateInfoDetail.mobileNumberPreferred
            ? 'M'
            : 'T',
        nextOfKinName: candidateProfileDetails?.candidateInfoDetail.nextOfKinName,
        nextOfKinRelationshipToCandidate: relationshipDefaultObject,
        nextOfKinCountryCode: nextOfKinCountryCodeDefaultObject,
        nextOfKinContactNumber: candidateProfileDetails?.candidateInfoDetail.nextOfKinContactNumber,
        remarks: '',
        nationality: nationalityDefaultObject,
      } as CreateEditCandidateSchemaData),
    [
      IdTypeDefaultValue,
      candidateProfileDetails?.candidateInfoDetail.email,
      candidateProfileDetails?.candidateInfoDetail.hanyuPinyinName,
      candidateProfileDetails?.candidateInfoDetail.identity,
      candidateProfileDetails?.candidateInfoDetail.localAddressBlockNumber,
      candidateProfileDetails?.candidateInfoDetail.localAddressBuildingName,
      candidateProfileDetails?.candidateInfoDetail.localAddressFloorNumber,
      candidateProfileDetails?.candidateInfoDetail.localAddressPostalCode,
      candidateProfileDetails?.candidateInfoDetail.localAddressPreferred,
      candidateProfileDetails?.candidateInfoDetail.localAddressStreetName,
      candidateProfileDetails?.candidateInfoDetail.localAddressUnitNumber,
      candidateProfileDetails?.candidateInfoDetail.mobileNumber,
      candidateProfileDetails?.candidateInfoDetail.name,
      candidateProfileDetails?.candidateInfoDetail.nextOfKinContactNumber,
      candidateProfileDetails?.candidateInfoDetail.nextOfKinName,
      candidateProfileDetails?.candidateInfoDetail.telephoneNumber,
      citizenshipDefaultObject,
      defaultDateOfBirth,
      defaultMOESchoolObject,
      defaultNationalityIssueDate,
      nationalityDefaultObject,
      genderDefaultObject,
      isMOESchool,
      mobileNumberCountryCodeDefaultObject,
      nextOfKinCountryCodeDefaultObject,
      occupationDefaultObject,
      prStatusDefaultObject,
      raceDefaultObject,
      relationshipDefaultObject,
      schoolDefaultObject,
      telephoneNumberCountryCodeDefaultObject,
    ]
  );

  const formHook = useForm<CreateEditCandidateSchemaData>({
    mode: 'onSubmit',
    resolver: joiResolver(
      CreateEditCandidateSchema(
        isEdit || isEditState ? disabledFields : undefined,
        'Next of kin name',
        'Next of kin contact No'
      )
    ),
    defaultValues: formDefaultValues,
  });

  const { handleSubmit, getValues, reset, watch } = formHook;

  const watchActionType = watch('action');

  const handleEditParticulars = useCallback(() => {
    reset(formDefaultValues);
    setIsEditState(true);
    setFiles([]);
    setApiButtonStatus('idle');
    setUploadFileErrorMessage('');
  }, [formDefaultValues, reset]);

  const { mutate: mutatePutUpdateCandidateProfile } = usePutUpdateCandidateProfile({
    uuid,
    onSuccess: () => {
      setApiButtonStatus('success');
      setShouldSubmit(false);
      setIsEditState(false);
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('admin->>UserAccountManagement.ActionRequestMessage', {
          action: 'Edit particulars',
        }),
      });
      refetchCandidateProfileDetails().then(() => null);
    },
    onError: (error) => {
      setErrorMessage(error?.response?.data?.message!);
      setApiButtonStatus('error');
    },
  });

  const { mutate: mutatePostCreateCandidateProfile } = usePostCreateCandidateProfile({
    onSuccess: () => {
      setErrorMessage('');
      setApiButtonStatus('success');
      handleBackToCandidateListing();
    },
    onError: (error) => {
      setErrorMessage(error?.response?.data?.message!);
      setApiButtonStatus('error');
    },
  });

  const { mutate: postRequestFilePresigned } = usePostRequestFilePresigned({
    onSuccess: (fileDataContent) => {
      const tempFiles: UploadedFile[] = [];
      fileDataContent.forEach((_value, index) => {
        const pendingFile = files[index];
        const fileData = fileDataContent[index];
        pendingFile.presignedUrl = fileData.presignedUrl;
        pendingFile.key = fileData.key;
        tempFiles.push(pendingFile);
      });
      setFiles(tempFiles);
    },
    onError: () => {
      setApiButtonStatus('error');
    },
  });

  const fileKeys: string[] = [''];

  const {
    MOESchoolNameValue,
    MOESchoolCodeValue,
    occupationValue,
    payloadRequestObject,
  } = useCandidatePayloadValues({
    schoolDefaultObject,
    candidateProfileDetails: candidateProfileDetails?.candidateInfoDetail,
    formHook,
    isApproverNeededAction,
    uuid,
    fileKeys,
  });

  const idTypeValue = getValues('IdType');

  const idTypePayloadValue = useMemo(() => (idTypeValue === 'I' ? 'NRIC/FIN' : 'PASSPORT'), [
    idTypeValue,
  ]);

  const prStatusValue = getValues('prStatus')?.codeValueDescription;

  const prStatusPayloadValue = useMemo(
    () => (prStatusValue === 'FOREIGNER' ? 'ALIEN' : getValues('prStatus')?.codeValueDescription),
    [getValues, prStatusValue]
  );

  const handleConfirmChanges = useCallback(() => {
    if (uploadFileErrorMessage.length === 0) {
      const { filenames } = prepareFiles(files, setFiles);
      setShouldSubmit(true);
      setApiButtonStatus('loading');
      setOpenConfirmChangesModal(false);

      if (filenames.length) {
        postRequestFilePresigned({
          filenames,
          uuid,
        });
      }
    }
  }, [files, postRequestFilePresigned, uploadFileErrorMessage.length, uuid]);

  const handleBackToCandidateListing = useCallback(
    () =>
      history.push(`/${admin}/${userAccountManagement.route}`, {
        selectedRequestTypeMode: AccountTypeMode.Candidate,
      }),
    [history]
  );

  const handleBackToCandidateListingWithAlert = useCallback(
    () =>
      history.push(`/${admin}/${userAccountManagement.route}`, {
        selectedRequestTypeMode: AccountTypeMode.Candidate,
        action: getValues('action')?.description,
        email: candidateProfileDetails?.candidateInfoDetail?.email,
      }),
    [candidateProfileDetails?.candidateInfoDetail?.email, getValues, history]
  );

  const handleConfirmChangesButton = useCallback(() => {
    if (
      getValues('action')?.code === 'DELETE' &&
      !!candidateProfileDetails?.candidateInfoDetail?.existingRegistrationsMessage
    ) {
      setDeleteAccountModalMessage(
        candidateProfileDetails?.candidateInfoDetail?.existingRegistrationsMessage
      );
    } else if (isApproverNeededAction || isEditState) {
      setOpenConfirmChangesModal(true);
    } else {
      setShouldSubmit(true);
      setApiButtonStatus('loading');
    }
  }, [
    candidateProfileDetails?.candidateInfoDetail?.existingRegistrationsMessage,
    getValues,
    isApproverNeededAction,
    isEditState,
  ]);

  useEffect(() => {
    mutatePostSearchApprovers({
      functionName: FUNCTION_NAMES.USER_ACCOUNTS,
      functionModule: FunctionModule.ADMIN,
    });
  }, [mutatePostSearchApprovers]);

  useEffect(() => {
    const isAllFilesUploaded = files.every((f) => f.uploaded);
    const fileUploadHasError = files.every((f) => f.hasError);
    files.forEach((f) => {
      fileKeys.push(f.key as string);
    });

    if (fileUploadHasError) {
      setApiButtonStatus('error');
      setShouldSubmit(false);
    }

    if (isAllFilesUploaded && shouldSubmit) {
      if (!isEditState) {
        const AccountManagementActionPayload = {
          actionType: getValues('action')?.code,
          payload: payloadRequestObject,
        };
        mutatePostAccountManagementAction(AccountManagementActionPayload);
      } else if (isEdit) {
        const dateOfBirthInMilliseconds = new Date(getValues('dateOfBirth') || '');

        const nationalityIssueDateInMilliseconds = new Date(
          getValues('nationalityIssueDate') || ''
        );

        const updateCandidateProfilePayload = {
          candidateInfoDetail: {
            id: candidateProfileDetails?.candidateInfoDetail.id,
            accountType: candidateProfileDetails?.candidateInfoDetail.accountType,
            name: getValues('name'),
            userIdType: idTypePayloadValue,
            identity: getValues('NricFin'),
            hanyuPinyinName: '',
            gender: getValues('gender')?.codeValueDescription,
            dob: dateOfBirthInMilliseconds.getTime(),
            email: getValues('email'),
            race: getValues('race')?.codeValueDescription,
            nationality: getValues('nationality')?.codeValueDescription,
            prStatus: prStatusPayloadValue,
            nationalityIssueDate: nationalityIssueDateInMilliseconds.getTime() || null,
            citizenship: getValues('citizenship')?.codeValueDescription,
            school: MOESchoolNameValue,
            schoolCode: MOESchoolCodeValue,
            organisationCode: candidateProfileDetails?.candidateInfoDetail.organisationCode,
            occupation: occupationValue,
            localAddressBlockNumber: getValues('houseOrBlkNo'),
            localAddressUnitNumber: getValues('unit'),
            localAddressFloorNumber: getValues('floor'),
            localAddressBuildingName: getValues('building'),
            localAddressStreetName: getValues('streetName'),
            localAddressPostalCode: getValues('postalCode'),
            foreignAddress1: getValues('foreignAddress1'),
            foreignAddress2: getValues('foreignAddress2'),
            foreignAddress3: getValues('foreignAddress3'),
            telephoneCountryCode: getValues('telephoneCountryCode')?.codeValue || null,
            telephoneNumber: getValues('telephoneContactNumber'),
            mobileNumber: getValues('mobileContactNumber'),
            mobileNumberCountryCode: getValues('mobileCountryCode')?.codeValue,
            nextOfKinName: getValues('nextOfKinName'),
            nextOfKinContactNumber: getValues('nextOfKinContactNumber'),
            nextOfKinContactNumberCountryCode: getValues('nextOfKinCountryCode')?.codeValue,
            nextOfKinRelationship: getValues('nextOfKinRelationshipToCandidate')
              ?.codeValueDescription,
            blockStatus: candidateProfileDetails?.candidateInfoDetail.blockStatus,
            activeStatus: candidateProfileDetails?.candidateInfoDetail.activeStatus,
            lockStatus: candidateProfileDetails?.candidateInfoDetail.lockStatus,
            localAddressPreferred: getValues('preferredAddress') === 'L',
            mobileNumberPreferred: getValues('preferredTelephoneOrMobile') === 'M',
            candidateType: candidateProfileDetails?.candidateInfoDetail.candidateType,
            chineseName: '',
            secondRace: null,
          },
          requestDetail: {
            requestorRemarks: getValues('remarks'),
            approverUuid: getValues('approver')?.code,
            fileKeys,
          },
        };
        mutatePutUpdateCandidateProfile(updateCandidateProfilePayload);
      } else if (!isEdit) {
        const dateOfBirthInMilliseconds = new Date(getValues('dateOfBirth') || '');

        const nationalityIssueDateInMilliseconds = new Date(
          getValues('nationalityIssueDate') || ''
        );

        const createCandidateProfilePayload = {
          candidateInfoDetail: {
            accountType: candidateProfileDetails?.candidateInfoDetail.accountType,
            name: getValues('name'),
            statutoryName: getValues('name'),
            userIdType: idTypePayloadValue,
            identity: getValues('NricFin'),
            hanyuPinyinName: getValues('hanyupinyinName'),
            gender: getValues('gender')?.codeValueDescription,
            dob: dateOfBirthInMilliseconds.getTime(),
            email: getValues('email'),
            race: getValues('race')?.codeValueDescription,
            secondRace: getValues('race')?.codeValueDescription,
            nationality: getValues('nationality')?.codeValueDescription,
            prStatus: prStatusPayloadValue,
            nationalityIssueDate: nationalityIssueDateInMilliseconds.getTime() || null,
            citizenship: getValues('citizenship')?.codeValueDescription,
            school: getValues('school')?.name,
            schoolCode: getValues('school')?.id,
            occupation: occupationValue,
            localAddressBlockNumber: getValues('houseOrBlkNo'),
            localAddressUnitNumber: getValues('unit'),
            localAddressFloorNumber: getValues('floor'),
            localAddressBuildingName: getValues('building'),
            localAddressStreetName: getValues('streetName'),
            localAddressPostalCode: getValues('postalCode'),
            foreignAddress1: getValues('foreignAddress1'),
            foreignAddress2: getValues('foreignAddress2'),
            foreignAddress3: getValues('foreignAddress3'),
            telephoneCountryCode: getValues('telephoneCountryCode')?.codeValue || null,
            telephoneNumber: getValues('telephoneContactNumber'),
            mobileNumber: getValues('mobileContactNumber'),
            mobileNumberCountryCode: getValues('mobileCountryCode')?.codeValue,
            nextOfKinName: getValues('nextOfKinName'),
            nextOfKinContactNumber: getValues('nextOfKinContactNumber'),
            nextOfKinContactNumberCountryCode: getValues('nextOfKinCountryCode')?.codeValue,
            nextOfKinRelationship: getValues('nextOfKinRelationshipToCandidate')
              ?.codeValueDescription,
            localAddressPreferred: getValues('preferredAddress') === 'L',
            mobileNumberPreferred: getValues('preferredTelephoneOrMobile') === 'M',
            candidateType: candidateProfileDetails?.candidateInfoDetail.candidateType,
          },
          requestDetail: {
            requestorRemarks: getValues('remarks'),
            approverUuid: getValues('approver')?.code,
            fileKeys,
          },
        };
        mutatePostCreateCandidateProfile(createCandidateProfilePayload);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldSubmit, files]);

  useEffect(() => {
    if (
      watchActionType?.code === 'RESEND_ACTIVATION_EMAIL' ||
      watchActionType?.code === 'RESET_PASSWORD' ||
      !watchActionType
    ) {
      setIsApproverNeededAction(false);
      setUploadFileErrorMessage('');
    } else {
      setIsApproverNeededAction(true);
    }
  }, [watchActionType, watchActionType?.code]);

  useEffect(() => {
    if (files.length) {
      setUploadFileErrorMessage('');
    }
  }, [files.length, setUploadFileErrorMessage]);

  if (formDefaultValues?.name === undefined && isEdit) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  return (
    <>
      <TitleHeader
        title={t(
          isEdit
            ? 'admin->>UserAccountManagement.ViewDetails'
            : 'admin->>UserAccountManagement.CreateCandidateAccount'
        )}
      />
      <ContentTemplate type="full">
        <SectionHeader
          title={t('admin->>UserAccountManagement.AccountDetails')}
          containerJustifyContent="flex-start"
        >
          <>
            {hasEditPermission && !isEditState && (
              <Box className={classes.editParticularsButton}>
                <EditButton
                  editHandler={handleEditParticulars}
                  label={t('admin->>UserAccountManagement.EditParticulars')}
                  typographyVariant="h5"
                />
              </Box>
            )}
          </>
        </SectionHeader>

        <form onSubmit={handleSubmit(handleConfirmChangesButton)}>
          <Accordion
            defaultExpanded
            header={t('admin->>UserAccountManagement.PersonalParticulars')}
          >
            <CandidatePersonalParticulars
              candidateProfileDetails={candidateProfileDetails?.candidateInfoDetail}
              formHook={formHook}
              isEditState={isEditState}
              isTaskDetails={false}
            />
          </Accordion>

          <Accordion defaultExpanded header={t('admin->>UserAccountManagement.ContactInformation')}>
            <CandidateContactInformation
              candidateProfileDetails={candidateProfileDetails?.candidateInfoDetail}
              formHook={formHook}
              isEditState={isEditState}
              disabledFields={{}}
            />
          </Accordion>
          <Accordion defaultExpanded header={t('admin->>UserAccountManagement.NextOfKinDetails')}>
            <CandidateNextOfKinDetails
              formHook={formHook}
              isEditState={isEditState}
              candidateProfileDetails={candidateProfileDetails?.candidateInfoDetail}
            />
          </Accordion>
          {!isEditState && (
            <ManageAccountContent
              userType={candidateProfileDetails?.candidateInfoDetail?.userType as string}
              accountType={candidateProfileDetails?.candidateInfoDetail?.accountType as string}
              activeStatus={candidateProfileDetails?.candidateInfoDetail?.activeStatus as string}
              lockStatus={candidateProfileDetails?.candidateInfoDetail?.lockStatus as string}
              blockStatus={candidateProfileDetails?.candidateInfoDetail?.blockStatus as string}
            />
          )}
          <ActionApproverRemarksContent
            filteredActionTypes={filteredActionTypes}
            apiButtonStatus={apiButtonStatus}
            postSearchApproversData={postSearchApproversData}
            files={files}
            setFiles={setFiles}
            isEditState={isEditState}
            isApproverNeededAction={isApproverNeededAction}
            formHook={formHook}
            isEdit={isEdit}
            isAdmin={false}
          />
          <AccountManagementMiscellaneousComponents
            errorMessage={errorMessage}
            uploadFileErrorMessage={uploadFileErrorMessage}
            apiButtonStatus={apiButtonStatus}
            authorities={authorities}
            setFiles={setFiles}
            isEdit={isEdit}
            isEditState={isEditState}
            setUploadFileErrorMessage={setUploadFileErrorMessage}
            setErrorMessage={setErrorMessage}
            setIsEditState={setIsEditState}
            handleBackToListing={handleBackToCandidateListing}
            profileDetails={candidateProfileDetails}
            deleteAccountModalMessage={deleteAccountModalMessage}
            setDeleteAccountModalMessage={setDeleteAccountModalMessage}
            setOpenConfirmChangesModal={setOpenConfirmChangesModal}
          />
          <ChangeRequestDialog
            open={openConfirmChangesModal}
            onClose={() => setOpenConfirmChangesModal(false)}
            onConfirm={handleConfirmChanges}
          />
        </form>
      </ContentTemplate>
    </>
  );
};
export default CreateEditViewCandidateAccount;
